// Pure helpers extracted from BoardView so they can be unit tested in isolation.

import { CaseMarker, getCaseMarker } from './caseMarkers.ts';
import { needsNerveBlock } from './staffNameplates.ts';

export const isLatestGeneration = (myGen: number, currentGen: number): boolean => {
  return myGen === currentGen;
};

/**
 * What the board does when an allocation job it started reports COMPLETED.
 *
 * A run belongs to the date it was started on (`runDate`), and the job can take minutes — long enough for the
 * runner to move the board to another day. Everything the run produces is therefore written for `runDate`: that
 * day's cached shifts/cases/allocations are refreshed, and its per-day overlays (float CRNAs, OB holds, NO CRED,
 * relief handoffs, Undo, move log) are rewritten in storage. But the board's ON-SCREEN copies of those overlays
 * are only replaced when `runDate` is still the date on screen; otherwise the day the runner moved to would be
 * overwritten with the run's results. (They are reloaded from storage when the runner returns to `runDate`.)
 */
export interface AllocationCompletionPlan {
  /** The day whose cached data must be refreshed — always the run's own date, never the one on screen. */
  refreshDate: string;
  /** Rewrite the run date's per-day overlays from the result (fresh runs only; a modify pass keeps them). */
  rebuildOverlays: boolean;
  /** Capture the grading baseline for the run date (fresh runs only). */
  captureBaseline: boolean;
  /** Replace the board's on-screen overlay state — only when the run's date is the one being shown. */
  updateDisplayedDay: boolean;
  /** Success toast; names the date when the runner has moved to another day. */
  toast: string;
}

export const planAllocationCompletion = (p: { runDate: string; currentDate: string; isModifyFlow: boolean }): AllocationCompletionPlan => {
  const sameDay = p.runDate === p.currentDate;
  const base = p.isModifyFlow ? 'Allocations modified successfully!' : 'Staff allocations generated successfully!';
  return {
    refreshDate: p.runDate,
    rebuildOverlays: !p.isModifyFlow,
    captureBaseline: !p.isModifyFlow,
    updateDisplayedDay: sameDay,
    toast: sameDay ? base : `${base.replace(/!$/, '')} for ${p.runDate}.`,
  };
};

/**
 * Whether the board may move the time bar to the day's first case start on its own.
 *
 * Only on the simulation (local) board, which has no meaningful "now". A deployed board's bar means
 * the live clock, and it stays there when live tracking switches off -- "Who's free?" switches it off
 * without the user touching the bar, and parking then would yank them from mid-afternoon back to the
 * morning. Keyed on the MODE rather than on liveNow so that no future way of leaving live tracking
 * can reintroduce that. And never once the user has moved the bar: a scrub wins until the date changes.
 */
export const shouldAutoParkTimeBar = (s: { localMode: boolean; liveNow: boolean; userMovedBar: boolean }): boolean =>
  s.localMode && !s.liveNow && !s.userMovedBar;

export const shouldShowRoomInActive = (
  hospitalId: string,
  hasSchedules: boolean,
  forcedActiveHospitalIds: ReadonlySet<string>
): boolean => {
  if (forcedActiveHospitalIds.has(hospitalId)) return true;
  return hasSchedules;
};

export const toggleHospitalIdInSet = (
  set: ReadonlySet<string>,
  hospitalId: string
): Set<string> => {
  const next = new Set(set);
  if (next.has(hospitalId)) {
    next.delete(hospitalId);
  } else {
    next.add(hospitalId);
  }
  return next;
};

export interface ModalCloseState {
  modifiedAllocations: Map<string, unknown>;
  isModifying: boolean;
}

export const computeStateAfterModalClose = (
  current: { isModifying: boolean }
): ModalCloseState | null => {
  // Returns null when no state change is needed (caller should leave existing state alone).
  if (!current.isModifying) return null;
  return {
    modifiedAllocations: new Map(),
    isModifying: false,
  };
};

// ---------------------------------------------------------------------------
// Allocate button enablement
//
// Decides whether the "Allocate Staff" / "Apply Modifications" button is
// disabled. Kept pure so the pin-then-fill flow is unit-testable without
// rendering BoardView.
//
// Rules:
//  - Always disabled while a date isn't chosen, the board is loading, or an
//    allocation run is already in flight.
//  - In modify mode the button stays enabled (re-running with edits is the
//    whole point of modify mode).
//  - If the user has staged in-memory pins (pinCount > 0) the button is ENABLED
//    so they can run the algorithm with those pins — whether the board is empty
//    (pin-then-fill) OR already allocated from a prior run (pin-on-top + re-run;
//    the greedy backend keeps the pins and recomputes the rest idempotently).
//  - Otherwise, once allocations are persisted AND we're not modifying, the
//    button is disabled: re-running with NO pins would just reshuffle the board,
//    so the user must stage a pin, modify, or wipe first.
// ---------------------------------------------------------------------------
export interface AllocateButtonState {
  hasSelectedDate: boolean;
  allocating: boolean;
  loading: boolean;
  hasPersistedAllocations: boolean;
  isModifying: boolean;
  pinCount: number;
}

export const isAllocateDisabled = ({
  hasSelectedDate,
  allocating,
  loading,
  hasPersistedAllocations,
  isModifying,
  pinCount,
}: AllocateButtonState): boolean => {
  if (!hasSelectedDate || allocating || loading) return true;
  // Staged pins enable the run regardless of whether a prior board exists.
  if (pinCount > 0) return false;
  // Day already allocated, no pins, not modifying: lock the button.
  if (hasPersistedAllocations && !isModifying) return true;
  return false;
};

// ---------------------------------------------------------------------------
// Pin modal prefill resolution
//
// Decides what staff the manual-allocation (pin) modal should open pre-filled
// with, for a given schedule. Pure so the pin-on-top + re-run flow is testable.
//
// Priority:
//  1. Modify mode -> the in-progress entry from the modify map (or nothing).
//  2. Pre-run pin flow -> an already-staged in-memory pin for this schedule.
//  3. Otherwise fall back to the schedule's CURRENT persisted allocation, so
//     "Pin & re-run" on an allocated board starts from the staff already on it.
//  4. Nothing to prefill (a brand-new pin on an empty schedule).
// ---------------------------------------------------------------------------
export interface PinAllocationLike {
  scheduleId: string;
  allocatedNurseIds: string[];
  allocatedDoctorIds: string[];
}

interface PersistedAllocationLike {
  operatingRoomScheduleId: string;
  allocatedNurseIds?: string[];
  allocatedDoctorIds?: string[];
}

export const resolvePinPrefill = (args: {
  scheduleId?: string | null;
  isModifying: boolean;
  modifiedAllocations: ReadonlyMap<string, PinAllocationLike>;
  manualAllocations: ReadonlyArray<PinAllocationLike>;
  persistedAllocations: ReadonlyArray<PersistedAllocationLike>;
}): PinAllocationLike | undefined => {
  const { scheduleId, isModifying, modifiedAllocations, manualAllocations, persistedAllocations } = args;
  if (!scheduleId) return undefined;
  if (isModifying) return modifiedAllocations.get(scheduleId);

  const staged = manualAllocations.find(ma => ma.scheduleId === scheduleId);
  if (staged) return staged;

  const persisted = persistedAllocations.find(a => a.operatingRoomScheduleId === scheduleId);
  return persisted
    ? {
        scheduleId,
        allocatedNurseIds: persisted.allocatedNurseIds || [],
        allocatedDoctorIds: persisted.allocatedDoctorIds || [],
      }
    : undefined;
};

/** The four colours a case block can take. */
export type BoardCardState = 'success' | 'incomplete' | 'manual' | 'empty';

/**
 * Colour for one case block.
 *
 * The rule after a run: STAFFING decides the colour, a pin does not. Green needs (1) a doctor on the room AND
 * (2) the case actually COVERED — the person physically in the room (the CRNA if supervised, or the SOLO
 * doctor if there is no CRNA/student) is on shift for the whole case. So a room whose shift-work CRNA works
 * until 3pm shows green through 1500 and no longer once that CRNA has gone home, even though the supervising
 * doctor (who is covering other rooms) is still around. `covered` is computed by the caller, which knows the
 * case times and each person's shift; it defaults to true so callers without that context keep the old
 * has-a-doctor behaviour. A pin is drawn as a small corner dot instead (`pinned` on BoardScheduleBlock).
 *
 * Before any run there is no staffing to show, so a pin is still blue — it is the only cue that something
 * is staged for the next Allocate.
 */
/**
 * Minutes of prep a provider gets before the first case of the room they OPEN, when that is short of the report-in
 * lead — 30 min, or 45 when the first case needs a nerve block. A short prep means a float needs to go help them set
 * up, so the board outlines their name block in red (Braden 2026-09-14). null when they have the full lead, when
 * their arrival is unknown, or when they arrive after the case starts (that is a coverage gap shown red on the case).
 */
export const shortPrepMinutes = (firstCaseStartMs: number, arrivalMs: number | undefined, nerveBlockFirstCase: boolean): number | null => {
  if (arrivalMs === undefined || !Number.isFinite(arrivalMs) || !Number.isFinite(firstCaseStartMs)) return null;
  const prep = Math.round((firstCaseStartMs - arrivalMs) / 60000);
  const lead = nerveBlockFirstCase ? 45 : 30;
  return prep >= 0 && prep < lead ? prep : null;
};

/**
 * Report-in lead for one case (Braden 2026-09-14): 30 min before a case that OPENS a room (45 when it needs a nerve
 * block), and none for a case in a room that is already running — someone taking over a room just steps in.
 */
export const reportInLeadMin = (takingOver: boolean, nerveBlock: boolean): number => (takingOver ? 0 : nerveBlock ? 45 : 30);

/**
 * The cases that are TAKEOVERS: a room is already running once its first staffed case (anyone — doctor, CRNA or
 * student) has started, so every later case in that room is taken over, not opened. Unstaffed cases never open a room.
 */
export const takeoverScheduleIds = (
  allocations: { operatingRoomScheduleId: string; allocatedDoctorIds?: string[]; allocatedNurseIds?: string[] }[],
  roomIdOf: (scheduleId: string) => string | undefined,
  startMsOf: (scheduleId: string) => number | undefined,
): Set<string> => {
  const firstStaffedStart = new Map<string, number>();
  for (const a of allocations) {
    if (!(a.allocatedDoctorIds?.length || a.allocatedNurseIds?.length)) continue;
    const room = roomIdOf(a.operatingRoomScheduleId), start = startMsOf(a.operatingRoomScheduleId);
    if (room === undefined || start === undefined || !Number.isFinite(start)) continue;
    firstStaffedStart.set(room, Math.min(firstStaffedStart.get(room) ?? Infinity, start));
  }
  const takeovers = new Set<string>();
  for (const a of allocations) {
    const room = roomIdOf(a.operatingRoomScheduleId), start = startMsOf(a.operatingRoomScheduleId);
    if (room !== undefined && start !== undefined && start > (firstStaffedStart.get(room) ?? Infinity)) takeovers.add(a.operatingRoomScheduleId);
  }
  return takeovers;
};

/**
 * A provider's report-in time: the earliest, across all their cases, of the case start minus its lead (reportInLeadMin).
 * A late arrival who opens a room still comes in 30 min before it; one who takes over steps in at the case start.
 * undefined when they have no cases.
 */
export const reportInMs = (cases: { startMs: number; nerveBlock: boolean; takingOver: boolean }[]): number | undefined => {
  let earliest: number | undefined;
  for (const c of cases) {
    if (!Number.isFinite(c.startMs)) continue;
    const t = c.startMs - reportInLeadMin(c.takingOver, c.nerveBlock) * 60000;
    if (earliest === undefined || t < earliest) earliest = t;
  }
  return earliest;
};

export const boardCardState = (args: {
  hasPersistedAllocations: boolean;
  manualAllocation: PinAllocationLike | null | undefined;
  allocation: PersistedAllocationLike | null | undefined;
  covered?: boolean;
}): BoardCardState => {
  const { hasPersistedAllocations, manualAllocation, allocation, covered = true } = args;
  if (!hasPersistedAllocations) return manualAllocation ? 'manual' : 'empty';
  // A staged pin shadows the persisted allocation: it is what the next run will hold fixed, so it is the
  // truer picture of who is on the room.
  const effective = manualAllocation || allocation;
  if (!effective) return 'incomplete';
  if ((effective.allocatedDoctorIds?.length || 0) === 0) return 'incomplete';   // no doctor yet
  return covered ? 'success' : 'incomplete';                                    // green only while covered
};

/**
 * Doctors who supervise a CRNA/student ANYWHERE on the board that day.
 *
 * Such a doctor is running other rooms concurrently, so an extra room they merely hold the signature on is
 * not a finished 1:1 solo room — it still NEEDS a CRNA. Only a doctor who supervises nobody all day is a
 * genuine solo doctor whose empty room reads "NONE" rather than "NEED CRNA".
 */
/**
 * Wipe EVERY per-day board overlay for one date. Each overlay is stored under `oc-<name>-<YYYY-MM-DD>`
 * (room/nurse transfers, relief, floats, site locks, dispositions/in-times, case markers, slot marks, move
 * log/undo, …). A fresh simulation replaces the whole day, so it must clear all of them or stale hand-edits
 * linger and mis-render against the new data (e.g. a stale room-transfer hiding a doctor's early rooms).
 *
 * The sweep is by key SUFFIX, so any overlay added later that follows the convention is caught automatically —
 * no per-key list to keep in sync. Non-dated board prefs (`oc-board-algorithm`, `oc-crna-view-mode`,
 * `oc-board-selected-date`, …) never end in a date, so they are left untouched. `store` is injectable for tests.
 */
export const clearAllDayOverlays = (
  date: string,
  store: Pick<Storage, 'key' | 'length' | 'removeItem'> = localStorage,
): void => {
  const suffix = `-${date}`;
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k && k.startsWith('oc-') && k.endsWith(suffix)) keys.push(k);
  }
  for (const k of keys) { try { store.removeItem(k); } catch { /* best-effort */ } }
};

export const daySupervisingDoctorIds = (
  allocations: ReadonlyArray<{ allocatedNurseIds?: string[]; allocatedDoctorIds?: string[] }>,
): Set<string> => {
  const ids = new Set<string>();
  for (const a of allocations || []) {
    if ((a?.allocatedNurseIds?.length || 0) === 0) continue;
    for (const id of a.allocatedDoctorIds || []) ids.add(id);
  }
  return ids;
};

export const formatShiftRange = (
  startTimeUtc: string,
  endTimeUtc: string,
  formatTime: (utc: string) => string
): string => {
  return `${formatTime(startTimeUtc)} – ${formatTime(endTimeUtc)}`;
};

// ---------------------------------------------------------------------------
// Schedule hover details
//
// Builds the structured data shown when a user hovers a schedule block on the
// board: which doctors and nurses/students are on the case, each one's shift
// window, pecking order (for ordered doctors), and home hospital. Kept pure so
// the formatting/markup layer (BoardScheduleBlock) stays trivial and this logic
// is unit-testable without a DOM.
// ---------------------------------------------------------------------------

/** Where the staff list came from: a generated allocation, a pending manual
 *  allocation (before the algorithm has run), or nothing assigned. */
export type ScheduleStaffSource = 'allocated' | 'manual' | 'none';

/** Minimal shape of an employee needed to describe them in the hover tooltip. */
export interface HoverEmployeeLike {
  firstName: string;
  lastName: string;
  employeeType: string;
  homeHospitalId?: string;
}

/** Minimal shape of a shift needed for the hover tooltip. */
export interface HoverShiftLike {
  startTimeUtc: string;
  endTimeUtc: string;
  order?: number;
}

/** One staff member rendered in the tooltip. */
export interface HoverStaffDetail {
  employeeId: string;
  name: string;
  typeLabel: string;
  employeeType: string; // raw type ('nurse' | 'doctor' | 'nurse-student-junior' | 'nurse-student-senior')
  isDoctor: boolean;
  /** Pecking order for an ordered doctor, otherwise null. */
  order: number | null;
  /** A doctor working a shift with no pecking order ("shift doctor"). */
  isShiftDoctor: boolean;
  shiftStartUtc?: string;
  shiftEndUtc?: string;
  homeHospitalName?: string;
}

/** Everything the hover tooltip needs for a single schedule block. */
export interface ScheduleHoverDetails {
  surgeryType: string;
  startTimeUtc: string;
  endTimeUtc: string;
  source: ScheduleStaffSource;
  doctors: HoverStaffDetail[];
  nurses: HoverStaffDetail[];
  totalStaff: number;
}

const buildStaffDetail = (
  employeeId: string,
  isDoctor: boolean,
  employeesById: ReadonlyMap<string, HoverEmployeeLike>,
  shiftsByEmployeeId: ReadonlyMap<string, HoverShiftLike>,
  hospitalNamesById: ReadonlyMap<string, string>,
  typeDisplayName: (employeeType: string) => string
): HoverStaffDetail => {
  const emp = employeesById.get(employeeId);
  const shift = shiftsByEmployeeId.get(employeeId);
  // Pecking order is a doctor-only concept; ignore any stray order on non-doctors.
  const hasOrder = isDoctor && shift?.order !== undefined && shift?.order !== null;
  const homeHospitalName = emp?.homeHospitalId ? hospitalNamesById.get(emp.homeHospitalId) : undefined;
  return {
    employeeId,
    name: emp ? `${emp.firstName} ${emp.lastName}` : employeeId,
    typeLabel: emp ? typeDisplayName(emp.employeeType) : '',
    employeeType: emp ? emp.employeeType : '',
    isDoctor,
    order: hasOrder ? (shift!.order as number) : null,
    isShiftDoctor: isDoctor && !hasOrder,
    shiftStartUtc: shift?.startTimeUtc,
    shiftEndUtc: shift?.endTimeUtc,
    homeHospitalName: homeHospitalName || undefined,
  };
};

export const buildScheduleHoverDetails = (
  schedule: { surgeryType: string; startTimeUtc: string; endTimeUtc: string },
  doctorIds: readonly string[],
  nurseIds: readonly string[],
  source: ScheduleStaffSource,
  employeesById: ReadonlyMap<string, HoverEmployeeLike>,
  shiftsByEmployeeId: ReadonlyMap<string, HoverShiftLike>,
  hospitalNamesById: ReadonlyMap<string, string>,
  typeDisplayName: (employeeType: string) => string
): ScheduleHoverDetails => {
  const doctors = doctorIds.map((id) =>
    buildStaffDetail(id, true, employeesById, shiftsByEmployeeId, hospitalNamesById, typeDisplayName)
  );
  const nurses = nurseIds.map((id) =>
    buildStaffDetail(id, false, employeesById, shiftsByEmployeeId, hospitalNamesById, typeDisplayName)
  );
  return {
    surgeryType: schedule.surgeryType,
    startTimeUtc: schedule.startTimeUtc,
    endTimeUtc: schedule.endTimeUtc,
    source,
    doctors,
    nurses,
    totalStaff: doctors.length + nurses.length,
  };
};

// Display ordering for employee types in the Board's shift listings: clinical
// seniority (doctors → CRNAs → senior students → junior students). Types not
// in this map sort to the end.
export const SHIFT_VIEW_TYPE_ORDER: Record<string, number> = {
  doctor: 0,
  nurse: 1,
  'nurse-student-senior': 2,
  'nurse-student-junior': 3,
};

// Comparator used by Available / Allocated / All shift views so all three sort
// the same way: type first (per SHIFT_VIEW_TYPE_ORDER), then earliest shift
// end time within a type group. Invalid / unknown end times sort after valid
// ones so missing data does not pull rows to the top of a group.
export const compareShiftRowsByTypeThenEnd = (
  a: { employeeType: string; endTimeUtc: string },
  b: { employeeType: string; endTimeUtc: string }
): number => {
  const ta = SHIFT_VIEW_TYPE_ORDER[a.employeeType] ?? 99;
  const tb = SHIFT_VIEW_TYPE_ORDER[b.employeeType] ?? 99;
  if (ta !== tb) return ta - tb;
  const ea = new Date(a.endTimeUtc).getTime();
  const eb = new Date(b.endTimeUtc).getTime();
  if (Number.isNaN(ea) && Number.isNaN(eb)) return 0;
  if (Number.isNaN(ea)) return 1;
  if (Number.isNaN(eb)) return -1;
  return ea - eb;
};

// ---------------------------------------------------------------------------
// "The" shift for a provider on the board's day
//
// GET /shifts returns every shift that OVERLAPS the day window, in DynamoDB
// index order — so one provider can legitimately have more than one row (a
// night shift carried in from yesterday plus today's day shift), and the array
// order says nothing about which is which. Anything that shows a provider's
// shift therefore has to pick deliberately; the bullpen groups, labels and
// colors a brick by the EARLIEST shift, so the card that brick opens must
// resolve the same one or a brick disagrees with its own card.
//
// Rows with no start time sort last (missing data must never win the pick).
export const earliestShiftByEmployee = <T extends { employeeId: string; startTimeUtc?: string }>(
  shifts: readonly T[]
): Map<string, T> => {
  const byEmployee = new Map<string, T>();
  for (const shift of shifts) {
    const current = byEmployee.get(shift.employeeId);
    if (!current) { byEmployee.set(shift.employeeId, shift); continue; }
    const next = shift.startTimeUtc ?? '';
    const held = current.startTimeUtc ?? '';
    // ISO-8601 UTC strings compare correctly as strings; '' (no start) loses to any real timestamp.
    if (next && (!held || next < held)) byEmployee.set(shift.employeeId, shift);
  }
  return byEmployee;
};

// The block a provider comes BACK for later today, if any: the earliest shift starting at or after the end of
// their earliest one. Mirrors Symphony's own `returnsLaterMin` (§3), so the board and the allocator agree on what
// "coming back at 1500" means.
//
// Deliberately SEPARATE from earliestShiftByEmployee rather than widening it. That map is the single shift every
// board read resolves to (B5) so nameplates, labels, the bullpen and the provider card agree; widening it would
// reintroduce the very inconsistency it cured. This is an ADDITIONAL lookup with one job: keep someone visible in
// the bullpen for a shift they have not started yet. Braden 2026-09-20: "If someone is coming back for a later
// shift, don't go ahead and assign them but don't leave them invisible... show them as available in the bullpen
// for the 1500-2300 shift." A shift that OVERLAPS the block they are already working is not a return — that is the
// carried-in night shift earliestShiftByEmployee exists to disambiguate.
export const laterShiftByEmployee = <T extends { employeeId: string; startTimeUtc?: string; endTimeUtc?: string }>(
  shifts: readonly T[]
): Map<string, T> => {
  const earliest = earliestShiftByEmployee(shifts);
  const byEmployee = new Map<string, T>();
  for (const shift of shifts) {
    const first = earliest.get(shift.employeeId);
    if (!first || shift === first) continue;
    const firstEnd = first.endTimeUtc ?? '';
    const start = shift.startTimeUtc ?? '';
    if (!start || !firstEnd || start < firstEnd) continue;   // overlaps the block they are working now
    const held = byEmployee.get(shift.employeeId);
    if (!held || start < (held.startTimeUtc ?? '')) byEmployee.set(shift.employeeId, shift);
  }
  return byEmployee;
};

// Which of the rooms a CRNA/student currently holds must be VACATED to place them on a new case.
// A CRNA/student is single-room, so placing them somewhere has always pulled them out of wherever else they
// were — but "somewhere else" only conflicts when it runs AT THE SAME TIME. Before the returning-later bullpen
// (see `laterShiftByEmployee`), an already-staffed person never appeared in the bullpen, so stripping them out
// of EVERY case in the day was indistinguishable from stripping the one they were actually in. Now the runner
// can deliberately drag a CRNA who is covering a morning room into the afternoon room of their later shift, and
// a blanket strip would unstaff the morning room they are still standing in. So: vacate only the cases that
// overlap the one being placed. Touching endpoints (one case ends exactly as the next begins) do not overlap.
// A case with no usable times is vacated, which is the old conservative behaviour.
export const scheduleIdsToVacateForSingleRoomPlacement = (
  target: { startMs?: number; endMs?: number },
  held: readonly { scheduleId: string; startMs?: number; endMs?: number }[],
): Set<string> => {
  const vacate = new Set<string>();
  const tStart = target.startMs;
  const tEnd = target.endMs;
  for (const h of held) {
    if (tStart === undefined || tEnd === undefined || h.startMs === undefined || h.endMs === undefined) {
      vacate.add(h.scheduleId);
      continue;
    }
    if (h.startMs < tEnd && tStart < h.endMs) vacate.add(h.scheduleId);
  }
  return vacate;
};

// How many ANESTHESIOLOGISTS a set of doctor-short rooms actually needs — which is not the number of rooms.
// Braden 2026-09-19: "that may be 2 NEED MD in rooms and then 3 supervisors needed for the other 9 rooms. So would
// be a total of 5 needed MDs not 11." A room with nobody in it at all needs its own doctor (1:1); a room that
// already has a CRNA only needs a share of a supervisor, so those collapse at the SITE cap — one anesthesiologist
// cannot supervise across hospitals, so each site is counted separately. The per-room NEED MD boxes on the board
// are unaffected; this is only what the bullpen badge counts.
export const supervisorsNeeded = (
  rooms: { hospitalId: string; hasCrna: boolean }[],
  capFor: (hospitalId: string) => number,
): number => {
  let solo = 0;
  const crnaRoomsBySite = new Map<string, number>();
  for (const r of rooms) {
    if (r.hasCrna) crnaRoomsBySite.set(r.hospitalId, (crnaRoomsBySite.get(r.hospitalId) || 0) + 1);
    else solo++;
  }
  let total = solo;
  for (const [hid, count] of crnaRoomsBySite) total += Math.ceil(count / Math.max(1, capFor(hid)));
  return total;
};

// Drag-to-reorder a pecking list: lift `draggedId` out and drop it in front of `targetId`, then renumber 1..N.
// Returns null when the move changes nothing or either id is missing, so the caller can skip the write.
// SHARED by the Anesthesiologists and CRNA tabs (Braden 2026-09-16: CRNAs get the same drag) so both lists
// renumber by identical rules rather than drifting apart.
export const reorderedPeckingIds = (ids: string[], draggedId: string, targetId: string): Record<string, number> | null => {
  if (draggedId === targetId) return null;
  const next = [...ids];
  const from = next.indexOf(draggedId);
  if (from < 0) return null;
  next.splice(from, 1);
  const insertAt = next.indexOf(targetId);
  if (insertAt < 0) return null;
  next.splice(insertAt, 0, draggedId);
  const order: Record<string, number> = {};
  next.forEach((id, i) => { order[id] = i + 1; });
  return order;
};

// The pecking-order number actually in force for a provider: a manual reorder
// on the Anesthesiologists tab (persisted per day, mirrored into the board as
// `peckingOrderOverride`) wins over the order stored on the shift itself.
// Returns undefined for "not in the pecking order", which is what the provider
// card treats as a shift-work provider.
/**
 * Sort key for the site header's anesthesiologist coins (Braden 2026-10-04). Four tiers, in order:
 *
 *   1. SOLO FIRST — a doctor alone in a room leads the row, whatever their shift.
 *   2. then SHIFT WORK before PECKING ORDER
 *   3. then WHEN THEY LEAVE within each
 *   4. then last name, purely as a tie-break
 *
 * `solo` is the board's own test, supervisedCrnaCount === 0 — deliberately NOT the plate colour, which
 * since shift-worker doctors took the out-3/5/7pm palette no longer identifies a solo doctor.
 *
 *   group 0 = SHIFT WORK, keyed by shift end (earliest out first)
 *   group 1 = PECKING ORDER, keyed by the rank itself
 *
 * The rank IS the out-order for pecking providers: they have full-day availability and no shift end, and
 * validate-allocations' send-home rule treats "higher rank (lower number) finishes AFTER lower rank" as the
 * violation — so #1 is expected out first and the highest number last.
 *
 * Times are LOCAL "HH:MM" so this stays pure and timezone-free. A shift whose end is at or before its start
 * crosses midnight and is pushed a day on, or an overnight doctor would sort as the EARLIEST out instead of
 * the latest. A doctor with no shift on file has no end and no rank, so they sit at the END of the shift-work
 * group — the board treats "no shift" as present all day, which is the opposite of leaving first.
 */
export const doctorCoinSortKey = (
  order: number | null | undefined,
  startHM?: string,
  endHM?: string,
  solo?: boolean,
): { solo: number; group: number; key: number } => {
  const soloTier = solo ? 0 : 1;
  if (order !== undefined && order !== null) return { solo: soloTier, group: 1, key: order };
  const mins = (hm?: string): number | undefined => {
    if (!hm) return undefined;
    const [h, m] = hm.split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : undefined;
  };
  const end = mins(endHM);
  if (end === undefined) return { solo: soloTier, group: 0, key: Number.MAX_SAFE_INTEGER };
  const start = mins(startHM) ?? 0;
  return { solo: soloTier, group: 0, key: end <= start ? end + 24 * 60 : end };
};

/**
 * Sort key for one row of a Manpower shift pane: SHIFT WORKERS first (by when they leave), then
 * PECKING-ORDER people in ascending numerical order.
 *
 * The two-group shape mirrors what the board already does -- Bullpen lists shift-window groups before
 * its numbered pecking group, and doctorCoinSortKey gives shift workers group 0 and pecking group 1 --
 * so the Manpower panes and the board read in the same direction instead of each inventing an order.
 *
 * `key` is a NUMBER for pecking, never a string: real data has orders past 9, and a lexicographic
 * compare would place #10 ahead of #2.
 *
 * Ties are real and expected, not a bug: pediatric pecking doctors run their own #1..N sequence
 * separate from the adult pool, so one day genuinely holds several #1s. This returns an equal key for
 * those, leaving the CALLER to break the tie deterministically (by name) -- otherwise equal numbers
 * would fall back to array order and the same day could render differently on each load.
 */
export const paneRowSortKey = (
  order: number | null | undefined,
  endTimeUtc?: string,
): { group: number; key: number } => {
  if (order !== undefined && order !== null) return { group: 1, key: order };
  const end = endTimeUtc ? new Date(endTimeUtc).getTime() : NaN;
  // A missing or unparseable end sorts last within the shift-work group, so absent data never
  // pulls a row to the top.
  return { group: 0, key: Number.isFinite(end) ? end : Number.MAX_SAFE_INTEGER };
};

export const effectivePeckingOrder = (
  override: Readonly<Record<string, number>> | undefined,
  employeeId: string,
  shiftOrder?: number | null
): number | undefined => {
  const manual = override?.[employeeId];
  if (manual !== undefined && manual !== null) return manual;
  return shiftOrder === undefined || shiftOrder === null ? undefined : shiftOrder;
};

// The `shift` prop for EmployeeDetailsPopover. The card formats each end with
// `new Date(...)`, which renders the literal string "Invalid Date" for a
// missing one, so a half-populated shift must be withheld entirely rather than
// passed through. The order carried is the EFFECTIVE pecking order, so the
// card's "Priority Order #N" row (and its "Out: TBD") always agree with the
// number printed on the bullpen brick.
export const providerCardShift = (
  shift: { startTimeUtc?: string; endTimeUtc?: string } | undefined,
  order: number | undefined
): { startTimeUtc: string; endTimeUtc: string; order?: number } | undefined => {
  if (!shift?.startTimeUtc || !shift?.endTimeUtc) return undefined;
  return { startTimeUtc: shift.startTimeUtc, endTimeUtc: shift.endTimeUtc, order };
};

export interface CaseFlagState {
  auto: boolean;
  overridden: boolean;
  value: boolean;
}

export interface EffectiveCaseFlags {
  nerveBlock: CaseFlagState;
  pediatric: CaseFlagState;
  hasOverride: boolean;
}

// Effective case-marker flags for a schedule: a hand-set override (caseMarkers.ts) wins over
// auto-detection. Auto nerve-block comes from surgery-type keywords (needsNerveBlock). Pediatric has
// NO auto-detection: we deliberately never collect or persist patient age from customer data (see
// backend/src/utils/jsonp-parser.ts, which explicitly skips that column on import), so the pediatric
// flag is manual-only until a runner hand-flags it on the board.
export const effectiveCaseFlags = (
  schedule: { scheduleId: string; surgeryType: string },
  caseMarkers: Record<string, CaseMarker>
): EffectiveCaseFlags => {
  const m = getCaseMarker(caseMarkers, schedule.scheduleId);
  const nbAuto = needsNerveBlock(schedule.surgeryType);
  return {
    nerveBlock: { auto: nbAuto, overridden: m?.nerveBlock !== undefined, value: m?.nerveBlock ?? nbAuto },
    pediatric: { auto: false, overridden: m?.pediatric !== undefined, value: m?.pediatric ?? false },
    hasOverride: !!m,
  };
};

// ---------------------------------------------------------------------------
// Board site (hospital column) order
//
// The Board groups its hospital COLUMNS in the order hospitals first appear in
// the operating-rooms list — `operatingRoomsByHospital` is a first-seen-key
// reduce, and the columns render in that key order with no sort. That order is
// an emergent property of the operating-rooms scan, but it IS what the runner
// sees, so the CRNA / Anesthesiologists tabs must group their sites the SAME
// way (otherwise a tab leads with Downtown while the board leads with Uptown).
//
// `hospitalOrderFromRooms` returns hospitalIds in that first-seen order; feed it
// the SAME operating-rooms array the board uses. `compareByHospitalOrder` turns
// that into a site comparator: known ids sort by their board index, unknown ids
// (a hospital with no rooms in the list) sort AFTER all known ones — matching how
// the board appends its synthetic "missing" column last — then by a caller
// fallback (e.g. name) so the tail stays stable.
// ---------------------------------------------------------------------------
export const hospitalOrderFromRooms = (rooms: readonly { hospitalId?: string }[]): string[] => {
  const order: string[] = [];
  const seen = new Set<string>();
  for (const r of rooms) {
    const hid = r.hospitalId;
    if (hid && !seen.has(hid)) { seen.add(hid); order.push(hid); }
  }
  return order;
};

export const compareByHospitalOrder = (
  order: readonly string[],
  fallback?: (a: string, b: string) => number
): ((a: string, b: string) => number) => {
  const rank = new Map<string, number>();
  order.forEach((hid, i) => rank.set(hid, i));
  const rankOf = (hid: string) => (rank.has(hid) ? rank.get(hid)! : Number.MAX_SAFE_INTEGER);
  return (a: string, b: string): number => {
    const diff = rankOf(a) - rankOf(b);
    if (diff !== 0) return diff;
    return fallback ? fallback(a, b) : 0;
  };
};

// ---------------------------------------------------------------------------
// The "Who's free?" time bar — geometry and drag rules
//
// The bar is the blue live "now" line; grabbing it parks it (glowing purple) and
// swaps the bullpen for the time-scoped availability panel. It is grabbed by ONE
// small knob, and that is a deliberate product decision (Braden, 2026-08-09):
// an interim fix made the WHOLE visible bar grabbable with a full-height strip,
// but that strip sat on top of the case blocks in whichever column the bar was
// parked in — at the 07:00 default, the busiest column of the day — so a click
// aimed at a 7am case hit a dead strip instead of opening the case.
//
// The knob must sit BELOW the board's sticky table header (`position: sticky`,
// z-index 6, 40px tall). That header covering the bar's original top-of-board
// handle is why the bar could not be grabbed at all — the bug this geometry
// exists to prevent, and the reason the knob is not simply pinned to top: 0.
//
// "Below" has to hold WHILE SCROLLING too: `.board-table` is its own vertical
// scroller (App.css) and the knob lives in its scrolling content, so BoardView
// adds the container's scrollTop to the offset below. A fixed offset alone puts
// the knob back under the header on any hospital tall enough to scroll.
// ---------------------------------------------------------------------------
// This must equal `.board-table th/td { height }` in App.css — whosFreeTimeBar.test.ts
// reads the stylesheet and fails if they drift, because two constants agreeing with
// each other in this file would prove nothing about the rendered header.
export const BOARD_STICKY_HEADER_PX = 40;
export const WHOS_FREE_HANDLE_TOP_PX = 42; // clears the sticky header (see above)
export const WHOS_FREE_HANDLE_HIT_PX = 24; // clickable box; the visible dot inside it is smaller

// Timeline geometry: the board maps a time to an x offset as
// `220px (sticky OR column) + 100px per hour` — see `getIndicatorLeftPx` in
// BoardView, which builds the same number from these constants. Dragging the bar
// has to invert it, so both directions read from here and cannot drift apart.
export const BOARD_OR_COLUMN_PX = 220;
export const BOARD_HOUR_COLUMN_PX = 100;

// x is in `.board-table-inner` CONTENT space (measured from the element's own left
// edge), so a horizontally-scrolled board needs no correction. Snaps to 15 minutes
// and clamps to the day, because the pointer can be dragged past either end of the
// timeline and a time outside 00:00–24:00 would place the bar off the board.
export const barMinutesFromContentX = (x: number): number => {
  const raw = ((x - BOARD_OR_COLUMN_PX) / BOARD_HOUR_COLUMN_PX) * 60;
  const snapped = Math.round(raw / 15) * 15;
  return Math.max(0, Math.min(24 * 60, snapped));
};

// Entering "Who's free?" stops live-clock tracking, opens the bullpen and swaps it
// for the availability panel — a heavyweight mode change to trigger by accident.
// The knob is small but it still sits over the board, so mousedown alone must NOT
// take over: wait until the pointer has actually travelled. A plain click on the
// knob therefore leaves the board exactly as it was.
export const BAR_DRAG_THRESHOLD_PX = 4;
export const barDragHasStarted = (originClientX: number, currentClientX: number): boolean =>
  Math.abs(currentClientX - originClientX) >= BAR_DRAG_THRESHOLD_PX;

// Hospital-level (SITE) credentialing — Braden 2026-08-29: "if they aren't credentialed at that hospital or
// surgery center, they can't be in ANY room there — CRNA, doc, or student."
//
// THREE implementations of this one rule have to agree, or the same provider gets three different answers on the
// same screen: Symphony's `siteCredentialingOk` (backend, decides who the ALGORITHM seats),
// `ManualAllocationModal` (decides who the picker OFFERS), and BoardView (decides which manual drops/transfers are
// ALLOWED). This is the frontend's single copy — both callers route through it. The shared contract:
//   - a credential names a site by hospitalId (migrated) OR by hospital NAME (legacy), so both must resolve;
//   - NO credentials on file => eligible EVERYWHERE. This is the ordinary state of a newly added employee, not an
//     edge case: create-employee omits the `credentials` attribute entirely when the list is empty. Drop this
//     escape and the board rejects every placement of such a provider while the allocator happily seats them.
//   - an unknown or absent hospital => don't block (benefit of the doubt).
// `resolveHospitalId` maps one credential string to the hospitalId it names, or undefined if it names no site.
export const isCredentialedAtHospital = (
  credentials: readonly string[] | null | undefined,
  hospitalId: string | null | undefined,
  resolveHospitalId: (credential: string) => string | undefined,
): boolean => {
  if (!hospitalId) return true;
  const creds = credentials || [];
  if (creds.length === 0) return true;
  return creds.some(c => resolveHospitalId(c) === hospitalId);
};
